import { reactive } from 'vue'
import { apiFetch } from './api'

export interface MachineryCategory {
  id: string
  name: string
  createdAt: string
}

export interface HeavyMachinery {
  id: string
  title: string
  capacityKg: number
  capacityPeople: number
  liftingHeightMeters: number
  categoryId: string
  categoryName?: string
  status: string
  notes?: string
  createdAt: string
}

export interface TaskMachinery {
  id: string
  title: string
  capacityKg: number
  capacityPeople: number
  liftingHeightMeters: number
  categoryId: string
  categoryName?: string
  quantity: number
}

const state = reactive({
  categories: [] as MachineryCategory[],
  machinery: [] as HeavyMachinery[],
  loading: false,
  error: ''
})

export const machineryStore = {
  state,

  async loadCategories() {
    state.loading = true
    state.error = ''
    try {
      const data = await apiFetch<MachineryCategory[]>('/machinery/categories')
      state.categories = data || []
    } catch (err) {
      state.error = `Erro ao carregar categorias: ${err}`
    } finally {
      state.loading = false
    }
  },

  async loadMachinery() {
    state.loading = true
    state.error = ''
    try {
      const data = await apiFetch<HeavyMachinery[]>('/machinery')
      state.machinery = data || []
    } catch (err) {
      state.error = `Erro ao carregar máquinas: ${err}`
    } finally {
      state.loading = false
    }
  },

  async createCategory(name: string) {
    try {
      const data = await apiFetch<MachineryCategory>('/machinery/categories', {
        method: 'POST',
        body: JSON.stringify({ name })
      })
      state.categories.push(data)
      return data
    } catch (err) {
      throw new Error(`Erro ao criar categoria: ${err}`)
    }
  },

  async updateCategory(id: string, name: string) {
    try {
      const data = await apiFetch<MachineryCategory>(`/machinery/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name })
      })
      const idx = state.categories.findIndex(c => c.id === id)
      if (idx !== -1) {
        state.categories[idx].name = name
      }
      return data
    } catch (err) {
      throw new Error(`Erro ao atualizar categoria: ${err}`)
    }
  },

  async deleteCategory(id: string) {
    try {
      await apiFetch<unknown>(`/machinery/categories/${id}`, { method: 'DELETE' })
      state.categories = state.categories.filter(c => c.id !== id)
    } catch (err) {
      throw new Error(`Erro ao deletar categoria: ${err}`)
    }
  },

  async createMachinery(machinery: Partial<HeavyMachinery>) {
    try {
      const data = await apiFetch<HeavyMachinery>('/machinery', {
        method: 'POST',
        body: JSON.stringify(machinery)
      })
      state.machinery.push(data)
      return data
    } catch (err) {
      throw new Error(`Erro ao criar máquina: ${err}`)
    }
  },

  async updateMachinery(id: string, machinery: Partial<HeavyMachinery>) {
    try {
      const data = await apiFetch<HeavyMachinery>(`/machinery/${id}`, {
        method: 'PUT',
        body: JSON.stringify(machinery)
      })
      const idx = state.machinery.findIndex(m => m.id === id)
      if (idx !== -1) {
        state.machinery[idx] = { ...state.machinery[idx], ...machinery }
      }
      return data
    } catch (err) {
      throw new Error(`Erro ao atualizar máquina: ${err}`)
    }
  },

  async deleteMachinery(id: string) {
    try {
      await apiFetch<unknown>(`/machinery/${id}`, { method: 'DELETE' })
      state.machinery = state.machinery.filter(m => m.id !== id)
    } catch (err) {
      throw new Error(`Erro ao deletar máquina: ${err}`)
    }
  },

  async getPlanning(id: string) {
    try {
      const data = await apiFetch<{ tasks?: Array<{ id: string; projectId: string; projectTitle: string; title: string; plannedStart: string; plannedEnd: string }> }>(`/machinery/${id}/planning`)
      return data
    } catch (err) {
      throw new Error(`Erro ao carregar planejamento: ${err}`)
    }
  }
}
